import { zodResolver } from "@hookform/resolvers/zod";
import { Button, TextField } from "@mui/material";
import { useId, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslations } from "use-intl";
import { z } from "zod";

import { Alert } from "@/components/Alert/Alert";
import { IconPromoCode } from "@/components/Icons";
import type { ValidationStatus } from "@/hooks/usePromoCodeValidation";

const createValidationSchema = (t: ReturnType<typeof useTranslations>) =>
  z.object({
    code: z.string().trim().min(1, t("promoCode.validation.required")),
  });

type PromoCodeFormValues = z.infer<ReturnType<typeof createValidationSchema>>;

export type Props = {
  appliedCode?: string;
  validationStatus?: ValidationStatus;
  onCodeApply: (values: PromoCodeFormValues) => void;
  onCodeRemove: () => void;
};

export function PromoCodeForm({
  appliedCode,
  validationStatus,
  onCodeApply,
  onCodeRemove,
}: Props) {
  const t = useTranslations();
  const headerId = useId();

  const promoCodeFormSchema = useMemo(() => createValidationSchema(t), [t]);

  const {
    handleSubmit,
    formState: { isValid },
    reset,
    control,
  } = useForm<
    z.input<typeof promoCodeFormSchema>,
    undefined,
    PromoCodeFormValues
  >({
    resolver: zodResolver(promoCodeFormSchema),
    defaultValues: {
      code: appliedCode || "",
    },
  });

  const isLoading = validationStatus?.type === "loading";

  return (
    <div className="section-card">
      <div
        id={headerId}
        className="mb-4 flex items-center gap-3 text-base font-medium text-text-primary"
      >
        <IconPromoCode className="size-10 shrink-0 rounded-lg bg-surface-accent-soft p-2 text-surface-accent-strong" />
        {t("promoCode.title")}
      </div>

      <form onSubmit={handleSubmit(onCodeApply)}>
        <Controller
          name="code"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              fullWidth
              aria-labelledby={headerId}
              hiddenLabel
              placeholder={t("promoCode.placeholder")}
              disabled={isLoading}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              slotProps={{
                input: {
                  readOnly: !!appliedCode,
                  endAdornment:
                    appliedCode && !isLoading ? (
                      <Button
                        key="remove-button"
                        type="button"
                        variant="text"
                        onClick={() => {
                          onCodeRemove();
                          reset({ code: "" });
                        }}
                      >
                        {t("remove")}
                      </Button>
                    ) : (
                      <Button
                        key="apply-button"
                        type="submit"
                        variant="text"
                        disabled={!isValid}
                        loading={isLoading}
                      >
                        {t("submit")}
                      </Button>
                    ),
                },
              }}
            />
          )}
        />

        {validationStatus?.type === "success" ||
        validationStatus?.type === "error" ? (
          <div className="mt-3">
            <Alert severity={validationStatus.type}>
              {validationStatus.message}
            </Alert>
          </div>
        ) : null}
      </form>
    </div>
  );
}
